import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ImageIcon, Loader2, Download,  Palette,
  Link2, ExternalLink, RefreshCw,
  FolderOpen, ChevronLeft, ArrowDown
} from 'lucide-react';

const API = '/api';

export default function ImageGenerator({ onImageGenerated }) {
  const [canvaConfig, setCanvaConfig] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [continuation, setContinuation] = useState(null);
  const [folderStack, setFolderStack] = useState([{ id: 'root', name: 'My Designs' }]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importedPreview, setImportedPreview] = useState(null);

  useEffect(() => {
    fetchCanvaConfig();
  }, []);

  const fetchCanvaConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/social/canva/config`, { withCredentials: true });
      setCanvaConfig(data);
    } catch {}
  };

  // ── Canva OAuth ──
  const connectCanva = async () => {
    try {
      const { data } = await axios.get(`${API}/social/canva/auth-url`, { withCredentials: true });
      window.open(data.auth_url, '_blank', 'width=600,height=700');
      toast.info('Complete Canva authorization in the popup, then click "Refresh".');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start Canva authorization');
    }
  };

  // ── Browse Canva Designs ──
  const fetchDesigns = useCallback(async (folderId = 'root', append = false) => {
    setLoadingDesigns(true);
    try {
      const params = { folder: folderId };
      if (append && continuation) params.continuation = continuation;
      const { data } = await axios.get(`${API}/social/canva/designs`, { params, withCredentials: true });
      if (append) {
        setDesigns(prev => [...prev, ...data.designs]);
      } else {
        setDesigns(data.designs);
      }
      setContinuation(data.continuation || null);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to load designs';
      toast.error(msg);
      if (msg.includes('expired') || msg.includes('reconnect')) {
        fetchCanvaConfig();
      }
    } finally { setLoadingDesigns(false); }
  }, [continuation]);

  const openFolder = (folderId, folderName) => {
    setFolderStack(prev => [...prev, { id: folderId, name: folderName }]);
    setContinuation(null);
    fetchDesigns(folderId);
  };

  const goBack = () => {
    if (folderStack.length <= 1) return;
    const newStack = folderStack.slice(0, -1);
    setFolderStack(newStack);
    setContinuation(null);
    fetchDesigns(newStack[newStack.length - 1].id);
  };

  // Auto-load designs when Canva is connected
  useEffect(() => {
    if (canvaConfig?.connected && designs.length === 0) {
      fetchDesigns('root');
    }
  }, [canvaConfig?.connected]);

  // ── Import Selected Design (export at native PNG resolution) ──
  const importDesign = async (design) => {
    if (!design?.id) return;
    setSelectedDesign(design.id);
    setImporting(true);
    try {
      // If it's a raw image asset, use the URL directly
      if (design.is_image && design.urls?.view_url) {
        setImportedPreview({ url: design.urls.view_url, title: design.title });
        onImageGenerated?.({ url: design.urls.view_url, source: 'canva' });
        toast.success(`Imported "${design.title}"`);
        setImporting(false);
        setSelectedDesign(null);
        return;
      }

      // For designs, export as PNG at native resolution
      const { data } = await axios.post(`${API}/social/canva/export-design`, {
        design_id: design.id, format: 'png',
      }, { withCredentials: true });

      if (data.success && data.download_url) {
        setImportedPreview({ url: data.download_url, title: design.title });
        onImageGenerated?.({ url: data.download_url, source: 'canva' });
        toast.success(`Imported "${design.title}" at full resolution`);
        // Save reference
        await axios.post(`${API}/social/canva/save-design`, {
          canva_design_id: design.id, export_url: data.download_url, title: design.title,
        }, { withCredentials: true }).catch(() => {});
      } else {
        toast.error('Export returned no image. Make sure the design is saved in Canva.');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to import design';
      toast.error(msg);
      if (msg.includes('expired') || msg.includes('reconnect')) fetchCanvaConfig();
    } finally {
      setImporting(false);
      setSelectedDesign(null);
    }
  };

  const downloadImported = () => {
    if (!importedPreview?.url) return;
    const link = document.createElement('a');
    link.href = importedPreview.url;
    link.download = `${importedPreview.title || 'design'}.png`;
    link.target = '_blank';
    link.click();
  };

  const currentFolder = folderStack[folderStack.length - 1];

  return (
    <div className="space-y-4" data-testid="image-generator">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-slate-900 flex items-center justify-center">
            <Palette className="h-3.5 w-3.5 text-white" />
          </div>
          <h3 className="text-sm font-semibold text-slate-800 tracking-tight">Design Studio</h3>
        </div>
        {canvaConfig?.connected && (
          <div className="flex items-center gap-1.5 text-xs text-emerald-600">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Canva connected</span>
          </div>
        )}
      </div>

      {/* Imported design preview */}
      {importedPreview?.url && (
        <div className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid="canvas-area">
          <img
            src={importedPreview.url}
            alt="Imported design"
            className="w-full max-h-72 object-contain"
            data-testid="canvas-image-preview"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div className="absolute bottom-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button size="sm" variant="secondary" className="h-7 text-xs shadow-sm" onClick={downloadImported} data-testid="download-image-button">
              <Download className="h-3 w-3 mr-1" /> Download
            </Button>
          </div>
          <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-md">
            {importedPreview.title}
          </div>
        </div>
      )}

      {/* Not configured */}
      {!canvaConfig?.configured && (
        <div className="text-center py-8 text-slate-400">
          <Palette className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium text-slate-500">Canva not configured</p>
          <p className="text-xs text-slate-400 mt-1">Set up Canva API credentials in environment to enable design imports</p>
        </div>
      )}

      {/* Configured but not connected */}
      {canvaConfig?.configured && !canvaConfig?.connected && (
        <div className="space-y-3">
          <div className="text-center py-6 bg-amber-50 rounded-lg border border-amber-200">
            <Link2 className="h-7 w-7 mx-auto mb-2 text-amber-500" />
            <p className="text-sm text-slate-700 font-medium">Connect your Canva account</p>
            <p className="text-xs text-slate-500 mt-1">Browse and import your designs at full resolution</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={connectCanva} data-testid="connect-canva-btn">
              <Link2 className="h-3.5 w-3.5 mr-1.5" /> Connect Canva
            </Button>
            <Button onClick={() => { fetchCanvaConfig(); }} variant="outline" data-testid="refresh-canva-status-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh Status
            </Button>
          </div>
        </div>
      )}

      {/* Connected — Design Browser */}
      {canvaConfig?.configured && canvaConfig?.connected && (
        <div className="space-y-2" data-testid="canva-browser">
          {/* Breadcrumb / navigation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              {folderStack.length > 1 && (
                <button onClick={goBack} className="flex items-center gap-0.5 text-blue-600 hover:text-blue-800" data-testid="canva-go-back">
                  <ChevronLeft className="h-3 w-3" /> Back
                </button>
              )}
              <span className="font-medium text-slate-700">{currentFolder.name}</span>
            </div>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => fetchDesigns(currentFolder.id)}
              disabled={loadingDesigns} data-testid="canva-refresh-designs">
              <RefreshCw className={`h-3 w-3 mr-1 ${loadingDesigns ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Loading */}
          {loadingDesigns && designs.length === 0 && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              <span className="ml-2 text-xs text-slate-400">Loading your designs...</span>
            </div>
          )}

          {/* Empty state */}
          {!loadingDesigns && designs.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-xs">No designs found in this folder</p>
              <Button size="sm" variant="link" className="text-xs mt-2"
                onClick={() => window.open('https://www.canva.com', '_blank')}>
                Create a design on canva.com <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            </div>
          )}

          {/* Design Grid */}
          {designs.length > 0 && (
            <div className="grid grid-cols-3 gap-2 max-h-[320px] overflow-y-auto pr-1" data-testid="canva-design-grid">
              {designs.map(design => (
                <button
                  key={design.id}
                  onClick={() => design.is_folder ? openFolder(design.id, design.title) : importDesign(design)}
                  disabled={importing && selectedDesign === design.id}
                  className={`group relative rounded-lg border overflow-hidden text-left transition-all hover:shadow-md hover:border-blue-300 ${
                    importing && selectedDesign === design.id ? 'ring-2 ring-blue-500 border-blue-500' : 'border-slate-200'
                  }`}
                  data-testid={`canva-design-${design.id}`}
                >
                  {/* Thumbnail */}
                  <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                    {design.is_folder ? (
                      <FolderOpen className="h-8 w-8 text-amber-400" />
                    ) : design.thumbnail_url ? (
                      <img
                        src={design.thumbnail_url}
                        alt={design.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <ImageIcon className="h-8 w-8 text-slate-300" />
                    )}

                    {/* Import overlay */}
                    {!design.is_folder && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {importing && selectedDesign === design.id ? (
                          <Loader2 className="h-5 w-5 text-white animate-spin" />
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <ArrowDown className="h-5 w-5 text-white" />
                            <span className="text-[10px] text-white font-medium">Import</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <div className="px-1.5 py-1">
                    <p className="text-[10px] text-slate-600 truncate leading-tight" title={design.title}>
                      {design.title}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Load More */}
          {continuation && (
            <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => fetchDesigns(currentFolder.id, true)}
              disabled={loadingDesigns} data-testid="canva-load-more">
              {loadingDesigns ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ArrowDown className="h-3 w-3 mr-1" />}
              Load More
            </Button>
          )}

          {/* Create new design link */}
          <div className="flex items-center justify-center pt-1">
            <Button size="sm" variant="link" className="text-xs text-slate-400 h-auto p-0"
              onClick={() => window.open('https://www.canva.com', '_blank')} data-testid="canva-create-new">
              Create new design on canva.com <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
